import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Target, Trophy, Flame, Star, Heart, Crown, Zap, Map as MapIcon,
  Sparkles, Gem, Users, Medal, Footprints, Calendar, Shield, Award,
  Clock, Compass, Moon, ShieldCheck, Bookmark, LifeBuoy, KeyRound, ChefHat, Lock,
  Infinity as InfinityIcon, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/services/api";
import { useAuth } from "@/context/AuthContext";

// Local catalogue (canonical icons + descriptions per user spec).
// Mapped to the badge codes from /app/backend/badges.py.
const BADGE_LIST = [
  { code: "gachette",        label: "Gachette",         desc: "Marquer 10 buts",                       Icon: Target,       color: "#22c55e" },
  { code: "soulier_or",      label: "Soulier d'Or",     desc: "Meilleur buteur 5 fois",                Icon: Trophy,       color: "#eab308" },
  { code: "serial_killer",   label: "Serial Killer",    desc: "Triple en un match",                    Icon: Flame,        color: "#ef4444" },
  { code: "lelu",            label: "L'Elu",            desc: "Elu MVP",                               Icon: Star,         color: "#f59e0b" },
  { code: "chouchou",        label: "Chouchou",         desc: "10 avis positifs",                      Icon: Heart,        color: "#ec4899" },
  { code: "ballon_or",       label: "Ballon d'Or",      desc: "10 fois MVP",                           Icon: Crown,        color: "#facc15" },
  { code: "le_patron",       label: "Le Patron",        desc: "5 victoires d'affilee",                 Icon: Zap,          color: "#a855f7" },
  { code: "boss_ville",      label: "Boss de la Ville", desc: "Jouer sur 5 terrains differents",       Icon: MapIcon,      color: "#06b6d4" },
  { code: "espoir",          label: "Espoir",           desc: "Note 7 sur un match",                   Icon: Sparkles,     color: "#22d3ee" },
  { code: "crack",           label: "Crack",            desc: "Note 8.5 sur un match",                 Icon: Sparkles,     color: "#8b5cf6" },
  { code: "masterclass",     label: "Masterclass",      desc: "Note 10/10 sur un match",               Icon: Gem,          color: "#10b981" },
  { code: "incontournable",  label: "Incontournable",   desc: "10 amis (cercle de joueurs)",           Icon: Users,        color: "#3b82f6" },
  { code: "legende",         label: "Legende",          desc: "100 matchs",                            Icon: InfinityIcon, color: "#f97316" },
  { code: "depucelage",      label: "Coup d'envoi",     desc: "1er match",                             Icon: Footprints,   color: "#84cc16" },
  { code: "habitue",         label: "Habitue",          desc: "1 match par semaine",                   Icon: Calendar,     color: "#14b8a6" },
  { code: "membre_cercle",   label: "Membre du Cercle", desc: "Profil complet",                        Icon: Shield,       color: "#0ea5e9" },
  { code: "legende_vivante", label: "Legende Vivante",  desc: "+1 an d'anciennete",                    Icon: Medal,        color: "#d946ef" },
  { code: "accro",           label: "Accro",            desc: "3 matchs en une semaine",               Icon: Clock,        color: "#f43f5e" },
  { code: "globe_trotteur",  label: "Globe-Trotteur",   desc: "Jouer dans 2 villes differentes",       Icon: Compass,      color: "#65a30d" },
  { code: "insomniaque",     label: "Insomniaque",      desc: "Match apres 21h",                       Icon: Moon,         color: "#6366f1" },
  { code: "soldat",          label: "Soldat",           desc: "100% de fiabilite",                     Icon: ShieldCheck,  color: "#22c55e" },
  { code: "cadre",           label: "Cadre",            desc: "5 matchs organises",                    Icon: Bookmark,     color: "#fb923c" },
  { code: "sauveur",         label: "Sauveur",          desc: "Rejoindre un match < 2h avant",         Icon: LifeBuoy,     color: "#0d9488" },
  { code: "grand_chef",      label: "Grand Chef",       desc: "Avoir note tous ses joueurs",           Icon: ChefHat,      color: "#7c3aed" },
  // Tier (cron-driven badges — recalculated chaque dimanche a 23h)
  { code: "legende_or",      label: "Legende",          desc: "Note Globale > 8.5",                    Icon: Crown,        color: "#FFD700" },
  { code: "infatigable",     label: "Infatigable",      desc: "Plus de 10 matchs joues",               Icon: Medal,        color: "#C0C0C0" },
];

export default function BadgesPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [earnedCodes, setEarnedCodes] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      navigate("/");
      return;
    }
    api.getUserBadges(user.id)
      .then((data) => {
        setEarnedCodes(new Set((data.earned || []).map(b => b.code)));
      })
      .catch((e) => toast.error(e.message || "Erreur badges"))
      .finally(() => setIsLoading(false));
  }, [isAuthenticated, user, navigate]);

  const earnedCount = earnedCodes.size;

  return (
    <div data-testid="badges-page" className="app-page" style={{ minHeight: "100dvh", backgroundColor: "#000", color: "#fff" }}>
      <header style={{ padding: "16px", display: "flex", alignItems: "center", gap: "12px", position: "sticky", top: 0, backgroundColor: "rgba(0,0,0,0.95)", zIndex: 10, borderBottom: "1px solid #1a1a1a" }}>
        <button data-testid="badges-back-btn" onClick={() => navigate("/profile")} style={{ padding: "8px", background: "none", border: "none", color: "#fff", cursor: "pointer" }}>
          <ArrowLeft size={20} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 className="font-heading" style={{ fontSize: "18px", fontWeight: 800 }}>Mes Badges</h1>
          <p style={{ fontSize: "11px", color: "#22c55e", fontWeight: 600 }}>
            {earnedCount} / {BADGE_LIST.length} debloques
          </p>
        </div>
      </header>

      {isLoading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>
          <RefreshCw size={20} className="animate-spin" style={{ display: "inline" }} />
          <p style={{ marginTop: "10px", fontSize: "13px" }}>Chargement...</p>
        </div>
      ) : (
        <div data-testid="badges-list" style={{ padding: "12px 16px 32px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {BADGE_LIST.map((b) => {
            const earned = earnedCodes.has(b.code);
            const Icon = b.Icon;
            return (
              <div
                key={b.code}
                data-testid={`badge-row-${b.code}`}
                data-earned={earned}
                style={{
                  display: "grid",
                  gridTemplateColumns: "56px 1fr",
                  gap: "14px",
                  alignItems: "center",
                  padding: "14px 16px",
                  borderRadius: "14px",
                  backgroundColor: earned ? "rgba(34,197,94,0.06)" : "#0a0a0a",
                  border: earned ? `1px solid ${b.color}55` : "1px solid #1a1a1a",
                  opacity: earned ? 1 : 0.7,
                  transition: "all 0.2s ease",
                }}
              >
                <div style={{
                  width: "56px", height: "56px", borderRadius: "14px",
                  backgroundColor: earned ? `${b.color}1a` : "#111",
                  border: earned ? `2px solid ${b.color}` : "1.5px solid #2a2a2a",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  position: "relative",
                  boxShadow: earned ? `0 4px 14px ${b.color}30` : "none",
                }}>
                  <Icon size={26} color={earned ? b.color : "#3a3a3a"} strokeWidth={earned ? 2.4 : 2} />
                  {!earned && (
                    <span style={{ position: "absolute", bottom: "-4px", right: "-4px", backgroundColor: "#0a0a0a", border: "1.5px solid #2a2a2a", borderRadius: "50%", padding: "3px" }}>
                      <Lock size={10} color="#6b7280" />
                    </span>
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                    <h3 style={{ fontSize: "14px", fontWeight: 700, color: earned ? "#fff" : "#9ca3af" }}>
                      {b.label}
                    </h3>
                    {earned && (
                      <Award size={12} color={b.color} />
                    )}
                  </div>
                  <p style={{ fontSize: "12px", color: earned ? "#9ca3af" : "#6b7280", lineHeight: 1.4 }}>
                    {b.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
